import type { StepDetailProps } from "@/types/crm";
import React, { memo, useState } from "react";
import { Badge } from "../ui/badge";
import { Link } from "react-router-dom";
import { MoreVertical, Pencil, Trash2, Trophy, XCircle } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Button } from "../ui/button";
import type { OpportunityStatus } from "@/hooks/useOpportunities";

interface ExtendedStepDetailProps extends StepDetailProps {
  status?: OpportunityStatus;
  onEdit?: () => void;
  onDelete?: () => void;
  isArchived?: boolean;
}

const StepDetail: React.FC<ExtendedStepDetailProps> = memo(({
  contactId,
  contactName,
  tags,
  contactTags,
  productName,
  amount,
  createdAt,
  attributedUserName,
  status,
  onEdit,
  onDelete,
  isArchived
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const linkTo = contactId ? `/crm/${contactId}` : '/crm';
  const hasMenu = Boolean(onEdit || onDelete);

  const getInitials = (name: string | null | undefined): string => {
    if (!name) return '?';
    const parts = name.trim().split(' ').filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  };

  const getStatusIndicator = () => {
    if (!isArchived) return null;
    if (status === 'won') {
      return (
        <div className="absolute top-2 right-2">
          <Trophy className="w-4 h-4 text-[#16A34A] dark:text-green-500" />
        </div>
      );
    }
    if (status === 'lost') {
      return (
        <div className="absolute top-2 right-2">
          <XCircle className="w-4 h-4 text-red-500" />
        </div>
      );
    }
    return null;
  };

  const getBorderStyle = () => {
    if (!isArchived) return 'border-border dark:border-[#3F3F464D]';
    if (status === 'won') return 'border-green-300 dark:border-green-600/50';
    if (status === 'lost') return 'border-red-300 dark:border-red-600/50';
    return 'border-border dark:border-[#3F3F464D]';
  };

  return (
    <div
      className={`relative rounded-2xl border ${getBorderStyle()} bg-white shadow-sm hover:shadow-md px-3.5 py-4 flex flex-col gap-3 text-foreground w-full transition-all duration-200 dark:bg-neutral-600 dark:text-neutral-50 dark:shadow-none dark:hover:shadow-none`}
    >
      {getStatusIndicator()}
      <div className="flex justify-between items-center gap-2">
        <Link
          to={linkTo}
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          className="flex-1 min-w-0 overflow-hidden group"
        >
          <div className="flex items-center gap-2">
            <span
              className="text-base font-semibold truncate flex-1 min-w-0 text-[#0F172A] dark:text-neutral-50 group-hover:text-[#2563EB] dark:group-hover:text-[#60A5FA] transition-colors duration-200"
              title={contactName}
            >
              {contactName}
            </span>
            <img
              src="/link.svg"
              alt="Contact Icon"
              className="flex-shrink-0 opacity-60 group-hover:opacity-100 dark:opacity-100 invert-[0.4] dark:invert-0"
            />
          </div>
        </Link>
        {hasMenu && (
          <Popover open={menuOpen} onOpenChange={setMenuOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-7 w-7 p-0 rounded-lg border-border bg-white hover:bg-[#F1F5F9] dark:bg-transparent dark:border-neutral-500 dark:hover:bg-neutral-500"
                onClick={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
              >
                <MoreVertical className="w-4 h-4 text-muted-foreground dark:text-neutral-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className="w-44 p-1 bg-card border-border rounded-xl shadow-md dark:bg-neutral-700 dark:border-neutral-600 dark:shadow-none"
              align="end"
              onClick={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
            >
              {onEdit && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start text-foreground hover:bg-hover dark:hover:bg-neutral-600"
                  onClick={(e) => {
                    e.stopPropagation();
                    setMenuOpen(false);
                    onEdit();
                  }}
                  onPointerDown={(e) => e.stopPropagation()}
                >
                  <Pencil className="w-4 h-4 mr-2 text-primary dark:text-blue-400" />
                  Editar
                </Button>
              )}
              {onDelete && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start text-red-500 hover:text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-neutral-600"
                  onClick={(e) => {
                    e.stopPropagation();
                    setMenuOpen(false);
                    onDelete();
                  }}
                  onPointerDown={(e) => e.stopPropagation()}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Excluir
                </Button>
              )}
            </PopoverContent>
          </Popover>
        )}
      </div>
      <div className="flex flex-col gap-2.5">
        <div className="flex flex-col gap-2 border-b border-border dark:border-[#3F3F464D] py-2">
          {productName && (
            <div className="flex items-center gap-2">
              <img src="/buy.svg" alt="Coin Icon" className="opacity-70 dark:opacity-100 invert-[0.4] dark:invert-0" />
              <span className="text-sm text-[#475569] dark:text-neutral-300">{productName}</span>
            </div>
          )}
          <div className="flex items-center gap-2">
            <img src="/coin.svg" alt="Coin Icon" className="opacity-70 dark:opacity-100 invert-[0.4] dark:invert-0" />
            <span className="text-sm font-semibold text-[#0F172A] dark:text-neutral-50">{amount}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-full bg-[#2563EB] dark:bg-blue-600 flex items-center justify-center text-[8px] font-semibold text-white flex-shrink-0">
            {getInitials(attributedUserName)}
          </div>
          <span className="text-sm truncate text-[#2563EB] dark:text-[#60A5FA]">
            {attributedUserName || (
              <span className="text-muted-foreground dark:text-neutral-400">Nao atribuido</span>
            )}
          </span>
        </div>
        <div className="flex items-center gap-2 text-[#64748B] dark:text-neutral-400">
          <img src="/date.svg" alt="" className="opacity-70 dark:opacity-100 invert-[0.4] dark:invert-0" />
          <span className="text-sm">{createdAt}</span>
        </div>
        {contactTags && contactTags.length > 0 && (
          <div className="flex items-start gap-2">
            <img src="/bookmark.svg" alt="" className="mt-1 flex-shrink-0 opacity-70 dark:opacity-100 invert-[0.4] dark:invert-0" />
            <div className="flex gap-1 flex-wrap max-h-[60px] overflow-hidden flex-1">
              {contactTags.slice(0, 3).map((tag, idx) => (
                <Badge variant="tag" className="capitalize px-2 py-0.5 text-xs !rounded-lg" key={idx}>
                  {tag}
                </Badge>
              ))}
              {contactTags.length > 3 && (
                <Badge variant="outline" className="px-2 py-0.5 text-xs border-border text-muted-foreground dark:border-neutral-500">
                  +{contactTags.length - 3}
                </Badge>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

StepDetail.displayName = 'StepDetail';

export default StepDetail;
